import {offset} from '@floating-ui/dom'
import React, {Suspense, useMemo, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import Shepherd from 'shepherd.js'
import 'shepherd.js/dist/css/shepherd.css'
import breakpoints from '../../breakpoints'
import {getMentor} from '../../store/mentors/mentors.selectors'
import {Mentor} from '../../store/mentors/mentors.type'
import {AppState} from '../../store/store'
import tourCompletionStore from '../../tours/tourCompletionStore'
import {ADD_LEARNER_CARD, ADD_LEARNER_ROW} from '../../tours/tours'
import FlatButton from '../FlatButton'
import Layout from '../Layout'
import LoadingIndicator from '../LoadingIndicator'
import TitleText from '../TitleText'
import TourEventHandler from '../TourEventHandler'
import TourProvider from '../TourProvider'
import {LearnerView} from './learnerView'
import {CARDS, TABLE} from './learnerViews'
import LearnersViewStore from './learnerViewStore'
import LearnerViewToggle from './LearnerViewToggle'
import MentorCodeBanner from './MentorCodeBanner'

const LearnerCardsView = React.lazy(() => import('./LearnerCardsView'))
const LearnerDashboardPage = React.lazy(
  () => import('../../legacy/components/pages/LearnerDashboardPage')
)

type Props = Readonly<{
  view: LearnerView
  onHowToAddLearners: () => void
  onViewToggle: (view: LearnerView) => void
}>

function LearnersPage({view, onHowToAddLearners, onViewToggle}: Props) {
  const mentor = useSelector<AppState, Mentor>(state => getMentor(state))

  return (
    <Layout
      control={<LearnerViewToggle view={view} onToggle={onViewToggle} />}
      hasFooter={view === CARDS}
      title={<Title onHowToAddLearners={onHowToAddLearners} />}
      onHowToAddLearners={onHowToAddLearners}
    >
      <MentorCodeBanner mentorCode={mentor.code} />
      <Suspense fallback={<LoadingIndicator />}>
        {view === CARDS && <LearnerCardsView />}
        {view === TABLE && <LearnerDashboardPage />}
      </Suspense>
    </Layout>
  )
}

function Title({
  onHowToAddLearners
}: Readonly<{onHowToAddLearners: () => void}>) {
  return (
    <div className="flex flex-col items-center lg:absolute lg:left-1/2 lg:-translate-x-1/2">
      <TitleText>
        <FormattedMessage id="learners.title" />
      </TitleText>
      <div className="hidden lg:flex">
        <FlatButton variant="transparent" onClick={() => onHowToAddLearners()}>
          <FormattedMessage id="learners.tutorial" />
        </FlatButton>
      </div>
    </div>
  )
}

function LearnersPageWithTour() {
  const [view, setView] = useState<LearnerView>(LearnersViewStore.getView)
  const mentor = useSelector(getMentor)

  const addLearnerCardTour = useAddLearnerCardTour()
  const addLearnerRowTour = useAddLearnerRowTour()

  function markAddLearnerTourComplete() {
    if (view === 'CARDS') {
      tourCompletionStore.markTourComplete(ADD_LEARNER_CARD, mentor.id)
    } else {
      tourCompletionStore.markTourComplete(ADD_LEARNER_ROW, mentor.id)
    }
  }

  return (
    <TourProvider
      name={view === 'CARDS' ? ADD_LEARNER_CARD : ADD_LEARNER_ROW}
      tour={view === 'CARDS' ? addLearnerCardTour : addLearnerRowTour}
    >
      <LearnersPage
        view={view}
        onViewToggle={setView}
        onHowToAddLearners={() => {
          if (view === 'CARDS') {
            tourCompletionStore.markAddLearnerToursIncomplete(mentor.id)
            addLearnerCardTour.start()
          } else {
            tourCompletionStore.markTableViewToursIncomplete(mentor.id)
            addLearnerRowTour.start()
          }
        }}
      />
      <TourEventHandler
        onCancel={markAddLearnerTourComplete}
        onComplete={markAddLearnerTourComplete}
      />
    </TourProvider>
  )
}

function useAddLearnerCardTour() {
  const intl = useIntl()
  return useMemo(() => {
    return new Shepherd.Tour({
      steps: [
        {
          classes: 'tooltip',
          id: 'toggle',
          attachTo: {
            element: '#learner-view-toggle',
            on: 'auto'
          },
          text: intl.formatMessage({id: 'learners.tours.addLearner.toggle'}),
          showOn() {
            // The toggle is only shown on large screens.
            return window.matchMedia(breakpoints.lg).matches
          },
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.skip'}),
              action() {
                this.next()
              }
            }
          ]
        },
        {
          classes: 'tooltip',
          id: 'names',
          attachTo: {
            element: '#add-learner-names',
            on: 'auto'
          },
          text: intl.formatMessage({id: 'learners.tours.addLearner.names'}),
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.next'}),
              action() {
                this.next()
              }
            }
          ]
        },
        {
          classes: 'tooltip space-between',
          id: 'guardian',
          attachTo: {
            element: '#add-learner-guardian',
            on: 'auto'
          },
          text: intl.formatMessage({
            id: 'learners.tours.addLearner.guardian'
          }),
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.back'}),
              action() {
                this.back()
              }
            },
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.next'}),
              action() {
                this.next()
              }
            }
          ]
        },
        {
          classes: 'tooltip space-between',
          id: 'save',
          attachTo: {
            element: '#add-learner-button',
            on: 'auto'
          },
          text: intl.formatMessage({
            id: 'learners.tours.addLearner.save'
          }),
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.back'}),
              action() {
                this.back()
              }
            },
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.next'}),
              action() {
                this.hide()
              }
            }
          ]
        }
      ],
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: {
          enabled: true
        },
        modalOverlayOpeningPadding: 12,
        modalOverlayOpeningRadius: 4,
        floatingUIOptions: {
          middleware: [offset(24)]
        }
      }
    })
  }, [intl])
}

function useAddLearnerRowTour() {
  const intl = useIntl()
  return useMemo(() => {
    return new Shepherd.Tour({
      steps: [
        {
          classes: 'tooltip',
          id: 'names',
          attachTo: {
            element: '#uploadCsv',
            on: 'bottom-start'
          },
          text: intl.formatMessage({id: 'learners.tours.uploadCsv'}),
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.next'}),
              action() {
                this.next()
              }
            }
          ]
        },
        {
          classes: 'tooltip',
          id: 'names',
          attachTo: {
            element: '.table-data-row',
            on: 'bottom-start'
          },
          text: intl.formatMessage({
            id: 'learners.tours.addLearner.names'
          }),
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.next'}),
              action() {
                this.next()
              }
            }
          ]
        },
        {
          classes: 'tooltip space-between',
          id: 'guardian',
          attachTo: {
            element: '#guardianEmail',
            on: 'bottom'
          },
          text: intl.formatMessage({
            id: 'learnerCreator.mentorCode_popup2_body'
          }),
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.back'}),
              action() {
                this.back()
              }
            },
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.next'}),
              action() {
                this.next()
              }
            }
          ]
        },
        {
          classes: 'tooltip space-between',
          id: 'save',
          attachTo: {
            element: '#saveNewLearner',
            on: 'auto'
          },
          text: intl.formatMessage({
            id: 'learners.tours.addLearner.save'
          }),
          buttons: [
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.back'}),
              action() {
                this.back()
              }
            },
            {
              classes: 'tour-button',
              text: intl.formatMessage({id: 'learners.tours.done'}),
              action() {
                this.next()
              }
            }
          ]
        }
      ],
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: {
          enabled: true
        },
        modalOverlayOpeningPadding: 12,
        modalOverlayOpeningRadius: 4,
        floatingUIOptions: {
          middleware: [offset(24)]
        },
        scrollTo: {
          behavior: 'smooth'
        }
      }
    })
  }, [intl])
}

export default LearnersPageWithTour
